import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FiCamera } from "react-icons/fi";
import Avator from "../../../assets/avatar.png";
import { BsCheckCircleFill } from "react-icons/bs";
import Cookies from "js-cookie";

export default function CompanyProfile() {
  const [kycCompleted, setKycCompleted] = useState(false);
  const [userData, setUserData] = useState({
    userId: "",
    company: "",
    email: "",
    name: "",
    phoneNumber: "",
    monthlyOrders: "0",
  });

  const [imagePreview, setImagePreview] = useState(Avator);
  const [profileImage, setProfileImage] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [website, setWebsite] = useState("");

  const hasFetched = useRef(false);
  const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchUserData = async () => {
      try {
        const token = Cookies.get("session");
        const response = await axios.get(`${REACT_APP_BACKEND_URL}/order/getUser`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setUserData((prev) => ({
          ...prev,
          userId: response.data.userId,
          name: response.data.fullname,
          company: response.data.company,
          email: response.data.email,
          phoneNumber: response.data.phoneNumber,
          monthlyOrders: response.data.monthlyOrders || "0",
          brandName: response.data.brandName || "",
          website: response.data.website || "",
        }));
        setImagePreview(response.data.profileImage || Avator);
        setKycCompleted(response.data.kycDone);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setImagePreview(imageURL);
      setProfileImage(file);
    }
  };

  const handleSave = async () => {
    try {
      const token = Cookies.get("session");
      const formData = new FormData();
      formData.append("brandName", brandName);
      formData.append("website", website);
      if (profileImage) formData.append("profileImage", profileImage);

      await axios.post(`${REACT_APP_BACKEND_URL}/user/update-profile`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Profile updated successfully");
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save profile");
    }
  };

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="relative w-full sm:h-40 h-28 bg-purple-100 flex items-end justify-center rounded-t-lg">
        <div className="relative -mb-10">
          <img
            src={imagePreview}
            alt="profile"
            className="w-24 h-24 rounded-full border-4 border-white object-cover"
          />
          <label
            htmlFor="profileUpload"
            className="absolute bottom-0 right-0 bg-white p-1 rounded-full cursor-pointer shadow-md"
          >
            <FiCamera className="text-gray-600 text-xl" />
          </label>
          <input
            type="file"
            id="profileUpload"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
      </div>

      {/* Name and Verified */}
      <div className="mt-12 w-full text-center">
        <h2 className="text-[14px] mt-12 sm:text-[18px] font-[600] text-gray-700 flex items-center justify-center gap-1">
          {userData.name}
          {kycCompleted && (
            <BsCheckCircleFill className="text-[#632C9D] text-[18px]" title="Verified" />
          )}
        </h2>
      </div>

      {/* 3 Columns: Summary | User Details | POC + Sales */}
      <div className="w-full flex flex-col md:flex-row gap-4">
        {/* Summary */}
        <div className="flex-1 bg-white p-4 text-gray-500 text-[12px] sm:text-[14px] border border-gray-300 rounded-lg space-y-2">
          <p><strong>User ID:</strong> <span className="text-gray-700">{userData.userId}</span></p>
          <p><strong>KYC Status:</strong> <span className={`${kycCompleted ? "text-[#632C9D]" : "text-red-500"}`}>{kycCompleted ? "Complete" : "Pending"}</span></p>
          {/* <p><strong>Target:</strong> <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded">{userData.monthlyOrders} SHIPMENTS</span></p> */}
          <p><strong>Agreement:</strong> <span className={`${kycCompleted ? "text-[#632C9D]" : "text-red-500"}`}>{kycCompleted ? "ACCEPTED" : "NOT ACCEPTED"}</span></p>
        </div>

        {/* User Details */}
        <div className="flex-1 bg-white p-4 rounded-lg border border-gray-300">
          <h3 className="text-[14px] sm:text-[16px] font-[600] mb-3 text-gray-700">User Details</h3>
          <div className="grid grid-cols-1 gap-2 text-[12px] sm:text-[14px] text-gray-600">
            <p><strong>Name:</strong> {userData.name}</p>
            <p><strong>Email:</strong> {userData.email}</p>
            <p><strong>Mobile:</strong> {userData.phoneNumber}</p>
            <p><strong>Company:</strong> {userData.company}</p>
            <p><strong>Brand Name:</strong> {userData.brandName || "-"}</p>
            <p><strong>Website:</strong> {userData.website || "-"}</p>
          </div>
        </div>

        {/* POC + Sales Executive Combined */}
        <div className="flex-1 bg-white p-4 rounded-lg border border-gray-300">
          <h3 className="text-[14px] sm:text-[16px] font-[600] mb-3 text-gray-700">POC & Sales Executive</h3>
          <div className="grid grid-cols-1 gap-2 text-[12px] sm:text-[14px] text-gray-600">
            <p><strong>POC Name:</strong> {userData.pocName || "-"}</p>
            <p><strong>POC Email:</strong> {userData.pocEmail || "-"}</p>
            <p><strong>POC Phone:</strong> {userData.pocPhone || "-"}</p>
            <p><strong>Sales Executive Name:</strong> {userData.salesExecutiveName || "-"}</p>
            <p><strong>Sales Executive Email:</strong> {userData.salesExecutiveEmail || "-"}</p>
            <p><strong>Sales Executive Phone:</strong> {userData.salesExecutivePhone || "-"}</p>
          </div>
        </div>

      </div>

      {/* Save Button */}
      <div className="text-right mt-4">
        <button
          onClick={handleSave}
          className="bg-[#632C9D] text-white py-2 px-3 rounded-lg text-[12px] font-[600] hover:bg-purple-500 transition"
        >
          Save
        </button>
      </div>
    </div>


  );
}
